"use client";

import { Command, CommandInput } from "@/components/ui/Command";
import { controlChevronClassName, controlSurfaceVariants } from "@/components/ui/control-surface";
import { ComboboxOptionList } from "@/components/ui/internal/combobox/ComboboxOptionList";
import type { ComboboxProps, Option } from "@/components/ui/internal/combobox/types";
import { filterComboboxOptions, getComboboxDisplayValue, groupComboboxOptions, prioritizeComboboxOptions, resolveComboboxOpenSessionSelection } from "@/components/ui/internal/combobox/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { ChevronDown, XCircle } from "lucide-react";
import * as React from "react";
import cn from "twsx";
type ComboboxOptionsLoader = NonNullable<ComboboxProps["getOptions"]>;
const asyncOptionsCache = new WeakMap<ComboboxOptionsLoader, Map<string, Option[]>>();
const asyncOptionsInFlight = new WeakMap<ComboboxOptionsLoader, Map<string, Promise<Option[]>>>();
function getSearchKey(search: string) {
  return search.trim();
}
function getLoaderCache(loader: ComboboxOptionsLoader) {
  let loaderCache = asyncOptionsCache.get(loader);
  if (!loaderCache) {
    loaderCache = new Map();
    asyncOptionsCache.set(loader, loaderCache);
  }
  return loaderCache;
}
function getInFlightCache(loader: ComboboxOptionsLoader) {
  let loaderCache = asyncOptionsInFlight.get(loader);
  if (!loaderCache) {
    loaderCache = new Map();
    asyncOptionsInFlight.set(loader, loaderCache);
  }
  return loaderCache;
}
async function loadComboboxOptions(loader: ComboboxOptionsLoader, search: string) {
  const searchKey = getSearchKey(search);
  const resultCache = getLoaderCache(loader);
  const cachedOptions = resultCache.get(searchKey);
  if (cachedOptions) {
    return cachedOptions;
  }
  const inFlightCache = getInFlightCache(loader);
  const existingRequest = inFlightCache.get(searchKey);
  if (existingRequest) {
    return existingRequest;
  }
  const request = Promise.resolve(loader(searchKey)).then(loadedOptions => {
    resultCache.set(searchKey, loadedOptions);
    return loadedOptions;
  }).finally(() => {
    inFlightCache.delete(searchKey);
  });
  inFlightCache.set(searchKey, request);
  return request;
}
function ComboboxComponent({
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  open: controlledOpen,
  onOpenChange,
  value,
  onChange,
  options: initialOptions = [],
  isLoading: controlledLoading,
  placeholder,
  multiSelectionDisplay = "values",
  disabled = false,
  showSelectionCheck = true,
  children,
  multi = false,
  onBlur,
  maxHeight = 300,
  className,
  contentClassName,
  align = "start",
  side = "bottom",
  getOptions,
  getOptionById,
  maxLength = 100,
  prioritizeSelectedOption = true
}: ComboboxProps) {
  const selectedValueDescriptionId = React.useId();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [options, setOptions] = React.useState(initialOptions);
  const [internalLoading, setInternalLoading] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<string | string[]>(multi ? [] : "");
  const [searchQuery, setSearchQuery] = React.useState("");
  const optionsCache = React.useRef<Record<string, (typeof initialOptions)[number]>>({});
  const hasSearchedRef = React.useRef(false);
  const requestIdRef = React.useRef(0);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIssuedSearchRef = React.useRef<string | null>(null);
  const selectedValueAtOpenRef = React.useRef<string | string[] | undefined>(undefined);
  const wasOpenRef = React.useRef(false);
  const openingInputRef = React.useRef<string | null>(null);
  const commandRef = React.useRef<HTMLDivElement>(null);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const isLoading = Boolean(controlledLoading || internalLoading);
  const selectedValue = value !== undefined ? value : internalValue;
  React.useLayoutEffect(() => {
    if (isOpen && !wasOpenRef.current && multi) {
      selectedValueAtOpenRef.current = Array.isArray(selectedValue) ? [...selectedValue] : [];
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, multi, selectedValue]);
  React.useEffect(() => {
    initialOptions.forEach(option => {
      optionsCache.current[option.value] = option;
    });
    if (initialOptions.length > 0) {
      setOptions(currentOptions => {
        const currentValues = new Set(currentOptions.map(option_0 => option_0.value));
        const missingOptions = initialOptions.filter(option_1 => !currentValues.has(option_1.value));
        return missingOptions.length === 0 ? currentOptions : [...currentOptions, ...missingOptions];
      });
    }
  }, [initialOptions]);
  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);
  React.useEffect(() => {
    const fetchOptionIfNeeded = async () => {
      if (!selectedValue || !getOptionById) {
        return;
      }
      setInternalLoading(true);
      try {
        if (multi) {
          const selected = Array.isArray(selectedValue) ? selectedValue : [];
          const missingValues = selected.filter(optionValue => !optionsCache.current[optionValue]);
          if (missingValues.length === 0) {
            return;
          }
          const fetchedOptions = await Promise.all(missingValues.map(optionValue_0 => getOptionById(optionValue_0)));
          fetchedOptions.forEach(option_2 => {
            if (option_2) {
              optionsCache.current[option_2.value] = option_2;
            }
          });
          setOptions(prev => {
            const next = [...prev];
            fetchedOptions.forEach(option_3 => {
              if (option_3 && !prev.some(candidate => candidate.value === option_3.value)) {
                next.push(option_3);
              }
            });
            return next;
          });
          return;
        }
        const selected_0 = selectedValue as string;
        if (optionsCache.current[selected_0]) {
          return;
        }
        const option_4 = await getOptionById(selected_0);
        if (option_4) {
          optionsCache.current[option_4.value] = option_4;
          setOptions(prev_0 => prev_0.some(candidate_0 => candidate_0.value === option_4.value) ? prev_0 : [...prev_0, option_4]);
        }
      } catch (error) {
        console.error("Error fetching option by ID:", error);
      } finally {
        setInternalLoading(false);
      }
    };
    fetchOptionIfNeeded();
  }, [selectedValue, multi, getOptionById]);
  const handleSearch = React.useCallback(async (search: string) => {
    if (!getOptions) {
      return;
    }
    const searchKey = getSearchKey(search);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const cachedOptions = getLoaderCache(getOptions).get(searchKey);
    if (cachedOptions) {
      setInternalLoading(false);
      setOptions(cachedOptions);
      cachedOptions.forEach(option_5 => {
        optionsCache.current[option_5.value] = option_5;
      });
      return;
    }
    setInternalLoading(true);
    try {
      const nextOptions = await loadComboboxOptions(getOptions, searchKey);
      if (requestId !== requestIdRef.current) return;
      setOptions(nextOptions);
      nextOptions.forEach(option_6 => {
        optionsCache.current[option_6.value] = option_6;
      });
    } catch (error_0) {
      if (requestId === requestIdRef.current) {
        console.error("Error fetching options:", error_0);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setInternalLoading(false);
      }
    }
  }, [getOptions]);
  const debouncedHandleSearch = React.useCallback((search_0: string) => {
    if (!isOpen || !getOptions) {
      return;
    }
    const searchKey_0 = getSearchKey(search_0);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      if (lastIssuedSearchRef.current === searchKey_0) return;
      lastIssuedSearchRef.current = searchKey_0;
      void handleSearch(searchKey_0);
    }, 100);
  }, [getOptions, handleSearch, isOpen]);
  React.useEffect(() => {
    if (isOpen && getOptions && !hasSearchedRef.current) {
      hasSearchedRef.current = true;
      lastIssuedSearchRef.current = "";
      void handleSearch("");
    }
    if (!isOpen) {
      hasSearchedRef.current = false;
    }
  }, [isOpen, getOptions, handleSearch]);
  React.useEffect(() => {
    if (isOpen) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    requestIdRef.current += 1;
    setInternalLoading(false);
  }, [isOpen]);
  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      requestIdRef.current += 1;
    };
  }, []);
  const displayOptions = React.useMemo(() => {
    if (!prioritizeSelectedOption) return options;
    return prioritizeComboboxOptions({
      options,
      selectedValue: resolveComboboxOpenSessionSelection({
        isOpen,
        multi,
        selectedValue,
        selectedValueAtOpen: selectedValueAtOpenRef.current,
        wasOpen: wasOpenRef.current
      }),
      multi,
      searchQuery,
      optionsCache
    });
  }, [isOpen, multi, options, prioritizeSelectedOption, searchQuery, selectedValue]);
  const filteredOptions = React.useMemo(() => {
    return filterComboboxOptions(displayOptions, searchQuery);
  }, [displayOptions, searchQuery]);
  const groupedOptions = React.useMemo(() => {
    return groupComboboxOptions(filteredOptions);
  }, [filteredOptions]);
  const displayValue = React.useMemo(() => {
    if (multi && multiSelectionDisplay === "count" && Array.isArray(selectedValue) && selectedValue.length) {
      return `${selectedValue.length} selected`;
    }
    return getComboboxDisplayValue({
      selectedValue,
      multi,
      placeholder,
      options,
      optionsCache
    });
  }, [multi, multiSelectionDisplay, options, placeholder, selectedValue]);
  const hasAccessibleSelectedValue = Boolean(ariaLabel && (Array.isArray(selectedValue) ? selectedValue.length > 0 : selectedValue));
  const accessibleSelectedValue = typeof displayValue === "string" ? displayValue : displayValue.label;
  const describedBy = [ariaDescribedBy, hasAccessibleSelectedValue ? selectedValueDescriptionId : undefined].filter((value_0): value_0 is string => Boolean(value_0)).join(" ") || undefined;
  const handleSelect = React.useCallback((optionValue_1: string) => {
    if (optionsCache.current[optionValue_1]?.disabled) return;
    if (multi) {
      const currentSelected = Array.isArray(selectedValue) ? selectedValue : typeof selectedValue === "string" && selectedValue ? [selectedValue] : [];
      if (currentSelected.includes(optionValue_1)) {
        const nextValues = currentSelected.filter(candidate_1 => candidate_1 !== optionValue_1);
        if (onChange) {
          (onChange as (newValue: string[]) => void)(nextValues);
        } else {
          setInternalValue(nextValues);
        }
        return;
      }
      if (currentSelected.length >= maxLength) {
        return;
      }
      const nextValues_0 = [...currentSelected, optionValue_1];
      if (onChange) {
        (onChange as (newValue: string[]) => void)(nextValues_0);
      } else {
        setInternalValue(nextValues_0);
      }
      return;
    }
    if (onChange) {
      (onChange as (newValue: string) => void)(optionValue_1);
    } else {
      setInternalValue(optionValue_1);
    }
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalOpen(false);
    }
  }, [maxLength, multi, onChange, onOpenChange, selectedValue]);
  const handleCheckboxClick = React.useCallback((event: React.MouseEvent, optionValue_2: string) => {
    event.preventDefault();
    event.stopPropagation();
    handleSelect(optionValue_2);
  }, [handleSelect]);
  const commandListStyle = React.useMemo<React.CSSProperties>(() => ({
    maxHeight
  }), [maxHeight]);
  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    if (nextOpen && multi) {
      selectedValueAtOpenRef.current = Array.isArray(selectedValue) ? [...selectedValue] : [];
    }
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
    if (!nextOpen) {
      setSearchQuery("");
      onBlur?.();
    }
  }, [multi, onBlur, onOpenChange, selectedValue]);
  const handleClear = React.useCallback((event_0: React.MouseEvent) => {
    event_0.preventDefault();
    event_0.stopPropagation();
    if (!multi) {
      if (onChange) {
        onChange("" as never);
        setInternalValue("");
      } else {
        setInternalValue("");
      }
    }
  }, [multi, onChange]);
  const filter = React.useCallback(() => 1, []);
  const triggerCaptureProps = {
    onPointerDownCapture: (event_1: React.PointerEvent) => {
      openingInputRef.current = event_1.pointerType;
    },
    onKeyDownCapture: () => {
      openingInputRef.current = "keyboard";
    }
  };
  return <Popover open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      {children === undefined || children === null ? <PopoverTrigger {...triggerCaptureProps} id={id} role="combobox" aria-label={ariaLabel} aria-describedby={describedBy} aria-expanded={isOpen} aria-invalid={ariaInvalid} aria-required={ariaRequired} onKeyDown={event_2 => {
      if (event_2.key === "Enter") {
        event_2.preventDefault();
        event_2.stopPropagation();
        handleOpenChange(!isOpen);
      }
    }} className={cn(controlSurfaceVariants({
      size: "md"
    }), "group/combobox-select flex items-center justify-between px-3", className, {
      "text-text-tertiary": !selectedValue || Array.isArray(selectedValue) && selectedValue.length === 0
    })} disabled={disabled || isLoading} onClick={() => handleOpenChange(!isOpen)}>
            {hasAccessibleSelectedValue && <span id={selectedValueDescriptionId} className="sr-only">
                {`Selected: ${accessibleSelectedValue}`}
              </span>}
            <span className="flex-1 truncate text-left font-normal">
              {typeof displayValue === "string" ? displayValue : displayValue.icon ? <div className="flex items-center gap-2">
                    {displayValue.icon}
                    <span>{displayValue.label}</span>
                  </div> : displayValue.label}
            </span>
            <div className="flex items-center gap-1">
              {!multi && selectedValue && <div className={cn("inline-flex size-5 items-center justify-center rounded-full", "text-text-tertiary hover:bg-fill-hover hover:text-text-primary")} onClick={handleClear} onMouseDown={event_3 => {
          event_3.preventDefault();
          event_3.stopPropagation();
        }} aria-label="Clear selection">
                  <XCircle className="size-3.5" />
                </div>}
              <ChevronDown className={cn(controlChevronClassName, "group-data-[state=open]/combobox-select:rotate-180")} />
            </div>
          </PopoverTrigger> : <PopoverTrigger asChild {...triggerCaptureProps}>{children}</PopoverTrigger>}
      <PopoverContent padding="none" className={cn("min-w-[min(var(--radix-popper-anchor-width),calc(100vw-16px))] max-w-[calc(100vw-16px)] overflow-hidden", contentClassName)} align={align} side={side} collisionPadding={8} sticky="always" onOpenAutoFocus={event_4 => {
      // Opening choices should not summon a phone keyboard. Keep focus
      // inside the modal; tapping Search still focuses the input normally.
      const input = openingInputRef.current;
      openingInputRef.current = null;
      if (input !== "keyboard" && (input === "touch" || input === "pen" || window.matchMedia("(max-width: 767px), (pointer: coarse)").matches)) {
        event_4.preventDefault();
        commandRef.current?.focus({
          preventScroll: true
        });
      }
    }}>
        <Command ref={commandRef} tabIndex={-1} filter={filter} className="h-auto max-h-[var(--radix-popover-content-available-height)]">
          <CommandInput className="text-[16px] md:text-base [@media(pointer:coarse)]:text-[16px]" wrapperClassName="flex-none" placeholder="Search..." onValueChange={nextValue => {
          setSearchQuery(nextValue);
          debouncedHandleSearch(nextValue);
        }} />
          <ComboboxOptionList commandListStyle={commandListStyle} filteredOptions={filteredOptions} groupedOptions={groupedOptions} isLoading={isLoading} maxLength={maxLength} multi={multi} selectedValue={selectedValue} showSelectionCheck={showSelectionCheck} onCheckboxClick={handleCheckboxClick} onSelect={handleSelect} />
        </Command>
      </PopoverContent>
    </Popover>;
}

/** Use when a person picks one or more options from a long, searchable list. */
const Combobox = React.memo(ComboboxComponent);
Combobox.displayName = "Combobox";
export { Combobox };
export type { ComboboxProps } from "@/components/ui/internal/combobox/types";
export type { Option } from "@/components/ui/internal/combobox/types";